import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { useHyperFormula } from '../hooks/useHyperFormula';
import numeral from 'numeral';
import { handleXBRLFieldBlur } from '../utils/xbrlMandatoryFields';
import { ValidationIndicator } from './ValidationIndicator';

// Attributable fields configuration (centralized)
const ATTRIBUTABLE_FIELDS = [
  'ProfitLossAttributableToOwnersOfParent',
  'ProfitLossAttributableToOtherComponentsOfEquity', 
  'ProfitLossAttributableToNoncontrollingInterests',
  'ComprehensiveIncomeAttributableToOwnersOfParent',
  'ComprehensiveIncomeAttributableToNoncontrollingInterests'
];

const ATTRIBUTABLE_FIELD_NAMES = ATTRIBUTABLE_FIELDS.map(field => `Attributable-${field}`);

// Fields that should NOT have footnote buttons (calculated/special fields)
const FIELDS_WITHOUT_FOOTNOTE = [
  'ProfitLoss',
  'ComprehensiveIncome',
  'CashAndCashEquivalents'
];

// Cash Flow fields configuration
const CASH_FLOW_FIELDS = [
  'CashAndCashEquivalents'
] as const;

const CASH_FLOW_LABELS: Record<string, Record<number, string>> = {
  'CashAndCashEquivalents': {
    1: 'Cash and cash equivalents at beginning of period',
    2: 'Cash and cash equivalents at end of period'
  }
};

// Statement of Retained Earnings (SORE) fields configuration
const SORE_FIELDS = [
  'RetainedEarnings'
] as const;

const SORE_LABELS: Record<string, Record<number, string>> = {
  'RetainedEarnings': {
    1: 'Retained earnings at beginning of period', // 2nd occurrence = opening
    2: 'Retained earnings at end of period'        // 3rd occurrence = closing
  }
};


const SORE_RESTATED_FIELDS = [
  'RetainedEarningsRestated'
] as const;

interface Column {
  label: string;
  member: string;
  period: string;
}


interface LineItemProps {
  item: any;
  columns: Column[];
  level: number;
  negativeTargets: string[];
  readOnlyFields: Set<string>;
  companyInfo: any;
  formData: Record<string, string>;
  onFieldChange: (name: string, contextRef: string, value: string) => void;
  onGetCalculatedValue: (fieldName: string, contextRef: string) => number | null;
  getTestOccurrence?: (itemName: string, itemKey: string) => number; // For testing occurrence
  itemIndex?: number; // Add index to make unique keys
  onFootnoteClick?: (
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => void;
  // hasFieldError/getFieldError removed - validation now handled via DOM manipulation
}

// Helper to format date strings (remove dashes for context refs, or return as-is if already formatted)
const formatDateForContext = (dateStr: string): string => {
  // If date is already in YYYYMMDD format, return as-is
  if (/^\d{8}$/.test(dateStr)) {
    return dateStr;
  }
  // Otherwise remove dashes
  return dateStr.replace(/-/g, '');
};

// Helper function to get REAL XBRL context ref (for data-context-ref)
// Builds real XBRL context refs: asof_YYYYMMDD_Member or fromto_YYYYMMDD_YYYYMMDD_Member
const getPeriodContext = (item: any, companyInfo: any, member: string, period: string): string => {
  const cleanMember = member.startsWith('_') ? member.substring(1) : member;
  const memberSuffix = cleanMember ? `_${cleanMember}` : '';
  
  // Get the appropriate period info
  const periodInfo = period === 'current' 
    ? (companyInfo.reporting_period || companyInfo.currentPeriod)
    : (companyInfo.previous_period || companyInfo.previousPeriod);
  
  if (!periodInfo) {
    // Fallback if period info not available
    return `${period}${memberSuffix}`;
  }
  
  const formattedStart = formatDateForContext(periodInfo.start);
  const formattedEnd = formatDateForContext(periodInfo.end);
  
  // Build context ref based on period_type (matches XBRL standard)
  if (item.properties?.period_type === 'duration') {
    // Duration period: fromto_START_END_Member
    return `fromto_${formattedStart}_${formattedEnd}${memberSuffix}`;
  } else {
    // Instant period (default): asof_END_Member
    return `asof_${formattedEnd}${memberSuffix}`;
  }
};

// Helper function to get STANDARD context (for data-year-end / HyperFormula)
// Converts real context to standard: current_SeparateMember, comparative_SeparateMember, etc.
// This is what HyperFormula uses for calculations (performance optimization)
const getStandardContext = (realContext: string, column: Column): string => {
  // Extract member from real context (last part after underscore)
  const parts = realContext.split('_');
  const member = parts[parts.length - 1]; // SeparateMember, ConsolidatedMember, etc.
  
  // Determine if current or comparative based on column period
  const period = column.period === 'current' ? 'current' : 'comparative';
  
  // Build standard context
  return `${period}_${member}`;
};

// Helper function to get current opening period context (matches get_current_opening_period_context macro)
// Used for Cash Flow opening fields (occurrence 1) in current column
const getCurrentOpeningPeriodContext = (item: any, companyInfo: any, member: string): string => {
  const period = companyInfo.currentPeriod || companyInfo.reporting_period;
  if (!period) {
    const cleanMember = member.startsWith('_') ? member.substring(1) : member;
    const memberSuffix = cleanMember ? `_${cleanMember}` : '';
    return `context${memberSuffix}`;
  }

  const formattedOpening = formatDateForContext(period.opening);
  const cleanMember = member.startsWith('_') ? member.substring(1) : member;
  const memberSuffix = cleanMember ? `_${cleanMember}` : '';

  return `asof_${formattedOpening}${memberSuffix}`;
};

// Helper function to get comparative opening period context (matches get_comparative_opening_period_context macro)
// Used for Cash Flow opening fields (occurrence 1) in previous/comparative column
const getComparativeOpeningPeriodContext = (item: any, companyInfo: any, member: string): string => {
  const period = companyInfo.previousPeriod || companyInfo.previous_period;
  if (!period) {
    const cleanMember = member.startsWith('_') ? member.substring(1) : member;
    const memberSuffix = cleanMember ? `_${cleanMember}` : '';
    return `context${memberSuffix}`;
  }

  const formattedOpening = formatDateForContext(period.opening);
  const cleanMember = member.startsWith('_') ? member.substring(1) : member;
  const memberSuffix = cleanMember ? `_${cleanMember}` : '';

  return `asof_${formattedOpening}${memberSuffix}`;
};

const FinancialTableInput: React.FC<{
  item: any;
  column: Column;
  contextRef: string;
  fieldId: string;
  isReadonly: boolean;
  value: string;
  onFieldChange: (name: string, contextRef: string, value: string) => void;
  fieldOccurrence?: number; // Use the testOccurrence count
  onFootnoteClick?: (
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => void;
}> = ({ item, column, contextRef, fieldId, isReadonly, value, onFieldChange, fieldOccurrence = 1, onFootnoteClick }) => {
  // UNCONTROLLED INPUTS: useRef is single source of truth, DOM mirrors it
  // - Editable fields: Uncontrolled, defaultValue from ref, onChange updates ref
  // - Readonly fields: Calculated by HyperFormula, updates ref then DOM
  const [isFocused, setIsFocused] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Error indicators now handled by DOM manipulation in useValidation.ts (no React rendering needed)
  
  // Note: defaultValue prop handles initial value from ref (single source of truth)
  // No need to manually set input.value - React handles defaultValue automatically

  // Format value for display
  const formatValue = useCallback((val: string, isReadonlyField: boolean): string => {
    if (!val || val.trim() === '') {
      // Only readonly fields show 0.00 by default, editable fields stay empty
      return isReadonlyField ? '0.00' : '';
    }
    
    const numericValue = numeral(val).value();
    if (numericValue === null || isNaN(numericValue)) {
      return isReadonlyField ? '0.00' : '';
    }
    
    return numeral(numericValue).format('0,0.00');
  }, []);

  // Update DOM value when prop changes (for readonly calculated fields like SOPL/SOCI/ClosingCash)
  // Pure DOM approach - directly update input.value, no React state
  useEffect(() => {
    if (isReadonly && inputRef.current && value) {
      // Direct DOM update - pure DOM like 1.0
      inputRef.current.value = value;
    }
  }, [value, isReadonly]);
  

  // Get field name based on occurrence and attributable fields (like your 1.0 system)
  const getFieldName = useCallback(() => {
    // Check for attributable fields (ignoring occurrence)
    if (ATTRIBUTABLE_FIELDS.includes(item.name)) {
      return `Attributable-${item.name}`;
    }
    
    // Check for Cash Flow fields with occurrence-based naming
    // Note: IncreaseDecreaseInCashAndCashEquivalents is handled by HyperFormula (readonly)
    if (CASH_FLOW_FIELDS.includes(item.name)) {
      if (fieldOccurrence === 1) {
        return `OpeningCash-${item.name}`;
      } else if (fieldOccurrence === 2) {
        return `ClosingCash-${item.name}`;
      }
    }

    // Check for SORE (Statement of Retained Earnings) fields
    // Decouple from HyperFormula to prevent duplicate ref errors
    // SORE has 1st occurrence (closing), SORE has 2nd (opening) and 3rd (closing)
    if (SORE_FIELDS.includes(item.name)) {
      if (fieldOccurrence === 1) {
        return `SORE-Opening-${item.name}`;
      } else if (fieldOccurrence === 2) {
        return `SORE-Closing-${item.name}`;
      }
    }
    
    // Check for occurrence-based fields
    if (item.name === 'ProfitLoss' && fieldOccurrence === 2) {
      return `SOPL-${item.name}`;
    }
    if (item.name === 'ComprehensiveIncome' && fieldOccurrence === 2) {
      return `SOCI-${item.name}`;
    }
    
    return item.name;
  }, [item.name, fieldOccurrence]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    // For uncontrolled editable inputs, show raw numeric value when focused (remove formatting)
    if (!isReadonly && inputRef.current) {
      const currentValue = inputRef.current.value || '';
      const numericValue = numeral(currentValue).value();
      if (numericValue !== null) {
        inputRef.current.value = numericValue.toString();
      }
    }
  }, [isReadonly]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    
    if (isReadonly) {
      return;
    }
    
    // CRITICAL: Clear any pending debounced update and process immediately
    // This ensures fast typing + blur doesn't lose the value
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    // Format the value on blur and update ref immediately (no debounce on blur)
    if (inputRef.current) {
      // Check XBRL mandatory fields first (set 0.00 if empty)
      handleXBRLFieldBlur(inputRef.current);
      
      const currentValue = inputRef.current.value || '';
      const formatted = formatValue(currentValue, false);
      
      // Always update DOM to show formatted value (even if empty)
      if (formatted !== currentValue) {
        inputRef.current.value = formatted;
      }
      
      // ALWAYS update ref on blur - even if empty (to ensure ref is synced)
      // This catches cases where user typed fast and blurred before debounce fired
      const fieldName = getFieldName();
      onFieldChange(fieldName, contextRef, formatted || currentValue || '');
    }
  }, [isReadonly, formatValue, getFieldName, contextRef, onFieldChange]);

  // Get special attributes for HyperFormula
  const getSpecialAttributes = useCallback(() => {
    const attrs: Record<string, string> = {};
    if (item.name === 'ProfitLoss' && fieldOccurrence === 2) {
      attrs['data-sopl-source'] = 'closing';
    }
    if (item.name === 'ComprehensiveIncome' && fieldOccurrence === 2) {
      attrs['data-soci-source'] = 'closing';
    }
    // Add cash source attributes (like 1.0 script)
    if (CASH_FLOW_FIELDS.includes(item.name)) {
      if (fieldOccurrence === 1) {
        attrs['data-cash-source'] = 'opening';
      } else if (fieldOccurrence === 2) {
        attrs['data-cash-source'] = 'closing';
      } else {
        attrs['data-cash-source'] = 'net';
      }
    }

    // Add data-sore-source for SORE fields (2nd = opening, 3rd = closing)
    if (SORE_FIELDS.includes(item.name)) {
      if (fieldOccurrence === 1) {
        attrs['data-sore-source'] = 'opening';
      } else if (fieldOccurrence === 2) {
        attrs['data-sore-source'] = 'closing';
      }
    }
    return attrs;
  }, [item.name, fieldOccurrence]);

  // Prevent non-numeric characters from being entered (only allow numbers, decimal, minus, comma)
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isReadonly) return;
    
    // Allow: Backspace, Delete, Tab, Escape, Enter, Arrow keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode)) {
      return;
    }
    
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88, 90].includes(e.keyCode)) {
      return;
    }
    
    // Allow: Home, End
    if ([35, 36].includes(e.keyCode)) {
      return;
    }
    
    // Allow: numbers (0-9) on main keyboard and numpad
    if ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    
    // Allow: decimal point (.) and comma (,)
    if ([190, 188, 110].includes(e.keyCode)) {
      return;
    }
    
    // Allow: minus sign (-) for negative numbers
    if ([189, 109].includes(e.keyCode)) {
      return;
    }
    
    // Prevent all other keys (including alphabets)
    e.preventDefault();
  }, [isReadonly]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadonly) {
      // Readonly fields shouldn't change
      return;
    }
    
    // UNCONTROLLED: DOM is already updated by user typing
    // Debounce updates to ref and HyperFormula (prevents lag while typing)
    const newValue = e.target.value;
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      const fieldName = getFieldName();
      // DON'T format while typing - just save raw value
      // Formatting ONLY happens on blur
      onFieldChange(fieldName, contextRef, newValue || '');
    }, 300); // 300ms debounce - update ref and HyperFormula after user pauses typing
  }, [getFieldName, contextRef, onFieldChange, isReadonly, formatValue]);
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);
  
  // Listen for external DOM updates (like data loading setting input.value directly)
  // For uncontrolled inputs, DOM is already updated, just trigger HyperFormula if needed
  useEffect(() => {
    if (isReadonly || !inputRef.current) return;
    
    const input = inputRef.current;
    
    // Listen for formDataLoaded event - when external scripts set DOM values
    const handleFormDataLoaded = () => {
      // Small delay to let DOM update complete
      setTimeout(() => {
        if (input && input.value && input.value.trim() !== '') {
          const domValue = input.value;
          // Trigger onFieldChange to update HyperFormula (DOM already has the value)
          const fieldName = getFieldName();
          onFieldChange(fieldName, contextRef, domValue);
        }
      }, 100);
    };
    
    document.addEventListener('formDataLoaded', handleFormDataLoaded);
    
    return () => {
      document.removeEventListener('formDataLoaded', handleFormDataLoaded);
    };
  }, [isReadonly, getFieldName, contextRef, onFieldChange]);

  const handleFootnoteButtonClick = useCallback(() => {
    if (onFootnoteClick && item.qname) {
      onFootnoteClick(
        item.standard_label,
        getFieldName(),
        contextRef,
        item.qname,
        fieldId,
        isReadonly
      );
    }
  }, [onFootnoteClick, item, contextRef, fieldId, isReadonly, getFieldName]);

  // Get footnote text from button attribute
  const getFootnoteTextFromButton = useCallback(() => {
    const button = document.querySelector(
      `.footnote-edit-btn[data-context-ref="${contextRef}"][data-qname="${item.qname}"]`
    ) as HTMLButtonElement;
    
    return button?.getAttribute('data-footnote-text') || '';
  }, [contextRef, item.qname]);

  const handleTooltipMouseEnter = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 100);
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    setShowTooltip(false);
  }, []);

  // Check if this field should have a footnote button
  const shouldShowFootnote = !FIELDS_WITHOUT_FOOTNOTE.includes(item.name);

  return (
    <div className="footnote-display-group">
      {shouldShowFootnote ? (
        <button 
          type="button" 
          className="footnote-edit-btn"
          data-field-id={fieldId}
          data-field-name={item.name}
          data-field-label={item.standard_label}
          data-context-ref={contextRef}
          data-qname={item.qname}
          onClick={handleFootnoteButtonClick}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <svg className="w-4 h-4" fill="none" stroke="#333333" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          {showTooltip && (
            <div className="footnote-tooltip show">
              {(() => {
                const footnoteText = getFootnoteTextFromButton();
                if (footnoteText && footnoteText.trim()) {
                  return (
                    <>
                      <div className="footnote-tooltip-header">Footnote:</div>
                      <div className="footnote-tooltip-content">{footnoteText.trim()}</div>
                    </>
                  );
                } else {
                  return (
                    <div className="footnote-tooltip-content footnote-tooltip-empty">
                      Click to add footnote
                    </div>
                  );
                }
              })()}
            </div>
          )}
        </button>
      ) : (
        <span className="footnote-button-spacer"></span>
      )}
      <div style={{ position: 'relative', flex: 1 }}>
        <input 
          ref={inputRef}
          type="text" 
          className="form-control financial-input" 
          name={getFieldName()}
          data-qname={item.qname}
          data-context-ref={contextRef}
          data-year-end={getStandardContext(contextRef, column)}
          data-calculation-target={item.name}
          data-no-fill={item.validation?.no_fill || 'N'}
          data-balance={item.properties?.balance}
          data-type={item.properties?.data_type}
          data-period-type={item.properties?.period_type}
          data-field-id={fieldId}
          defaultValue={isReadonly ? (value || '0.00') : (value || '')} // Uncontrolled: use defaultValue for initial value
          readOnly={isReadonly}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={isReadonly ? { backgroundColor: 'var(--bg-white-dark)', color: '#495057', fontWeight: 'bold' } : {}}
          {...getSpecialAttributes()}
        />
        <ValidationIndicator fieldName={getFieldName()} contextRef={contextRef} />
      </div>
    </div>
  );
};

const LineItem: React.FC<LineItemProps> = ({ 
  item, 
  columns, 
  level, 
  negativeTargets, 
  readOnlyFields, 
  companyInfo,
  formData,
  onFieldChange,
  onGetCalculatedValue,
  getTestOccurrence,
  itemIndex = 0,
  onFootnoteClick
}) => {
  // Create a stable unique key for this item based on its index and level
  const itemKey = `${level}_${itemIndex}`;
  
  // Get occurrence for this item (all non-abstract items, any level)
  const testOccurrence = !item.properties?.abstract && getTestOccurrence ? 
    getTestOccurrence(item.name, itemKey) : null;
  if (item.properties?.abstract) {
    return (
      <>
        <tr>
          <td 
            className={`
              ${level === 0 ? 'table-title-0 c-black' : ''}
              ${level === 1 ? 'table-title-1 c-theme-600' : ''}
              ${level === 2 ? 'table-title-2 c-theme-500' : ''}
              ${level === 3 ? 'table-title-3 c-dark' : ''}
              ${level >= 4 ? 'table-title-4 c-light' : ''}
            `}
            colSpan={columns.length + 1}
            style={{ paddingLeft: `${(level + 1) * 15}px` }}
          >
            {level === 4 && '- '}{item.standard_label}
          </td>
        </tr>
        {item.structure?.children && item.structure.children.map((child: any, index: number) => (
           <LineItem
             key={`${child.name}_${child.qname}_${level}_${index}`}
             item={child}
             columns={columns}
             level={level + 1}
             negativeTargets={negativeTargets}
             readOnlyFields={readOnlyFields}
             companyInfo={companyInfo}
             formData={formData}
             onFieldChange={onFieldChange}
             onGetCalculatedValue={onGetCalculatedValue}
             getTestOccurrence={getTestOccurrence}
             itemIndex={index}
             onFootnoteClick={onFootnoteClick}
           />
        ))}
      </>
    );
  }

  return (
    <tr>
      <td 
        className={`
          ${level === 0 ? 'table-title-0 c-black' : ''}
          ${level === 1 ? 'table-title-1 c-theme-600' : ''}
          ${level === 2 ? 'table-title-2 c-theme-500' : ''}
          ${level === 3 ? 'table-title-3 c-dark' : ''}
          ${level >= 4 ? 'table-title-4 c-light' : ''}
          ${negativeTargets.includes(item.name) ? 'negative-indicator' : ''}
        `}
        style={{ paddingLeft: `${(level + 1) * 15}px`, position: 'relative' }}
        title={negativeTargets.includes(item.name) ? 'Subtracted from total' : ''}
      >
        {/* Check for Cash Flow and SORE fields with custom labels */}
        {CASH_FLOW_FIELDS.includes(item.name as any) && testOccurrence && CASH_FLOW_LABELS[item.name]?.[testOccurrence] 
          ? CASH_FLOW_LABELS[item.name][testOccurrence]
          : SORE_FIELDS.includes(item.name as any) && testOccurrence && SORE_LABELS[item.name]?.[testOccurrence]
          ? SORE_LABELS[item.name][testOccurrence]
          : item.standard_label}
      </td>
      
      {columns.map((column, colIndex) => {
                // Get context ref based on period type and occurrence
                // For CashAndCashEquivalents occurrence 1 (opening), use opening period context
                let contextRef: string;
                if (SORE_FIELDS.includes(item.name) && testOccurrence === 1 || SORE_RESTATED_FIELDS.includes(item.name) && testOccurrence === 1) {
                  // Opening cash - use opening period context
                  if (column.period === 'current') {
                    contextRef = getCurrentOpeningPeriodContext(item, companyInfo, column.member);
                  } else {
                    contextRef = getComparativeOpeningPeriodContext(item, companyInfo, column.member);
                  }
                } else {
                  // Regular period context (current or comparative)
                  contextRef = getPeriodContext(item, companyInfo, column.member, column.period);
                }
                
                const fieldId = `${contextRef}_${item.qname}`;
                
                // Get the actual field name that will be used
                let actualFieldName = item.name;
                if (ATTRIBUTABLE_FIELDS.includes(item.name)) {
                  actualFieldName = `Attributable-${item.name}`;
                } else if (CASH_FLOW_FIELDS.includes(item.name)) {
                  if (testOccurrence === 1) {
                    actualFieldName = `OpeningCash-${item.name}`;
                  } else if (testOccurrence === 2) {
                    actualFieldName = `ClosingCash-${item.name}`;
                  }
                } else if (SORE_FIELDS.includes(item.name)) {
                  if (testOccurrence === 1) {
                    actualFieldName = `SORE-Opening-${item.name}`;
                  } else if (testOccurrence === 2) {
                    actualFieldName = `SORE-Closing-${item.name}`;
                  }
                } else if (testOccurrence === 2) {
                  if (item.name === 'ProfitLoss') {
                    actualFieldName = `SOPL-${item.name}`;
                  } else if (item.name === 'ComprehensiveIncome') {
                    actualFieldName = `SOCI-${item.name}`;
                  }
                }
                
                // Field readonly logic:
                // - SOPL-/SOCI- closing fields: readonly (calculated from Attributable- fields)
                // - ClosingCash- fields: readonly (calculated from IncreaseDecreaseInCashAndCashEquivalents + OpeningCash)
                // - RetainedEarningsRestated: readonly (sum of 2 above)
                // - SORE-Closing- fields: readonly (Restated + IncreaseDecrease)
                // - SORE-Opening- fields: manual inputs
                // - OpeningCash- fields: manual inputs
                // - Attributable- fields: manual inputs
                // - Other fields: follow HyperFormula rules
                const isClosingField = (actualFieldName.startsWith('SOPL-') && item.name === 'ProfitLoss' && testOccurrence === 2) ||
                                      (actualFieldName.startsWith('SOCI-') && item.name === 'ComprehensiveIncome' && testOccurrence === 2) ||
                                      (actualFieldName.startsWith('ClosingCash-') && CASH_FLOW_FIELDS.includes(item.name) && testOccurrence === 2) ||
                                      (actualFieldName === 'RetainedEarningsRestated') ||
                                      (actualFieldName.startsWith('SORE-Closing-'));
                
                const isReadonly = isClosingField ? true :
                  (actualFieldName.startsWith('Attributable-') || actualFieldName.startsWith('OpeningCash-') || actualFieldName.startsWith('SORE-Opening-')) ? false :
                  readOnlyFields.has(item.name);
                
        // Read from ref (single source of truth) via formData prop
        // For readonly fields, get calculated value from HyperFormula
        // For editable fields, get from ref (passed via formData prop)
        let fieldValue = '';
        if (isReadonly) {
          // Use the ACTUAL field name (SOPL-ProfitLoss, SOCI-ComprehensiveIncome, etc.)
          // NOT the item.name (ProfitLoss, ComprehensiveIncome)
          const calculatedValue = onGetCalculatedValue(actualFieldName, contextRef);
          fieldValue = calculatedValue !== null && calculatedValue !== undefined ? 
            numeral(calculatedValue).format('0,0.00') : '0.00';
        } else {
          // For editable fields, get from ref (formData prop contains ref values)
          // Find input to get standard context for ref key
          const tempInput = document.querySelector(
            `input[name="${actualFieldName}"][data-context-ref="${contextRef}"]`
          ) as HTMLInputElement;
          const standardContext = tempInput?.getAttribute('data-year-end');
          if (standardContext) {
            const refKey = `${actualFieldName}_${standardContext}`;
            fieldValue = formData[refKey] || '';
          }
        }
        
        return (
          <td key={colIndex} className="table-input">
            <div className={`
              ${level === 0 ? 'table-title-0 c-black' : ''}
              ${level === 1 ? 'table-title-1 c-theme-600' : ''}
              ${level === 2 ? 'table-title-2 c-theme-500' : ''}
              ${level === 3 ? 'table-title-3 c-dark' : ''}
              ${level >= 4 ? 'table-title-4 c-light' : ''}
            `}>
               {item.properties?.data_type === 'monetary' && (
                 <FinancialTableInput
                   item={item}
                   column={column}
                   contextRef={contextRef}
                   fieldId={fieldId}
                   isReadonly={isReadonly}
                   value={fieldValue}
                   onFieldChange={onFieldChange}
                   fieldOccurrence={testOccurrence || 1}
                   onFootnoteClick={onFootnoteClick}
                 />
               )}
            </div>
          </td>
        );
      })}
    </tr>
  );
};

interface SORETableProps {
  section: any;
  companyInfo: {
    consolidated: string;
    first_year: string;
    // Extended companyInfo with period dates
    reporting_period?: {
      opening: string;
      start: string;
      end: string;
    };
    previous_period?: {
      opening: string;
      start: string;
      end: string;
    };
    // Fallback for different structure
    currentPeriod?: {
      opening: string;
      start: string;
      end: string;
    };
    previousPeriod?: {
      opening: string;
      start: string;
      end: string;
    };
  };
  negativeTargets?: string[];
  // Add global HyperFormula props
  globalHyperFormula?: {
    isReady: boolean;
    readOnlyFields: Set<string>;
    handleFieldChange: (fieldName: string, contextRef: string, value: string) => void;
    getCalculatedValue: (fieldName: string, contextRef: string) => number | null;
  };
  onFieldChange?: (name: string, contextRef: string, value: string) => void;
  formData?: Record<string, string>; // Ref values (single source of truth): { "fieldName_standardContext": "value" }
  onFootnoteClick?: (
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => void;
  // hasFieldError/getFieldError removed - validation now handled via DOM manipulation
}

export const SORETable: React.FC<SORETableProps> = React.memo(({ 
  section, 
  companyInfo, 
  negativeTargets = [],
  globalHyperFormula,
  onFieldChange: globalFieldChange,
  formData: parentFormData,
  onFootnoteClick
}) => {
  // formData parameter is the ref (single source of truth) - passed from parent
  // parentFormData contains ref values: { "fieldName_standardContext": "value" }
  const [calculationTrigger, setCalculationTrigger] = useState(0);
  
  // Local occurrence counter for testing
  const occurrenceCounterRef = useRef<Map<string, number>>(new Map());
  
  // Get occurrence count for testing (stable - doesn't change on re-renders)
  const getTestOccurrence = useCallback((itemName: string, itemKey: string): number => {
    // Use a unique key that includes item position to ensure stability
    const uniqueKey = `${itemName}_${itemKey}`;
    
    if (!occurrenceCounterRef.current.has(uniqueKey)) {
      // Only increment the counter for this item name when we see a new unique key
      const itemCount = Array.from(occurrenceCounterRef.current.keys())
        .filter(key => key.startsWith(`${itemName}_`)).length + 1;
      
      occurrenceCounterRef.current.set(uniqueKey, itemCount);
    }
    
    return occurrenceCounterRef.current.get(uniqueKey)!;
  }, []);
  
  // Use global HyperFormula if provided, otherwise create local instance
  const localHyperFormula = useHyperFormula(
    globalHyperFormula ? null : { sections: { [section.name]: section } }
  );
  
  const hyperFormula = globalHyperFormula || {
    isReady: localHyperFormula.isReady,
    readOnlyFields: localHyperFormula.readOnlyFields,
    handleFieldChange: localHyperFormula.handleFieldChange,
    getCalculatedValue: localHyperFormula.getCalculatedValue
  };

  // Get calculated value from HyperFormula
  // contextRef here is the REAL XBRL context - we need to convert it to STANDARD context for HyperFormula
  const getCalculatedValue = useCallback((fieldName: string, contextRef: string): number | null => {
    if (!hyperFormula.isReady || !hyperFormula.getCalculatedValue) return null;
    
    // Check if this is a SOPL/SOCI closing field
    // Find the input to get its standard context (data-year-end) for same column
    const targetInput = document.querySelector(
      `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
    ) as HTMLInputElement;
    
    if (!targetInput) {
      return 0;
    }
    
    const standardContext = targetInput.getAttribute('data-year-end');
    if (!standardContext) {
      return 0;
    }
    
    // Find all Attributable- inputs in the same column (same data-year-end)
    const allAttributableInputs = Array.from(
      document.querySelectorAll(`input[name^="Attributable-"][data-year-end="${standardContext}"]`)
    ) as HTMLInputElement[];
    
    if (fieldName.startsWith('SOPL-')) {
      // SOPL: Sum 3 rows above (3 Attributable- fields)
      // Find the row position of SOPL field, then get 3 inputs above it
      const soplRow = targetInput.closest('tr');
      if (!soplRow) return 0;
      
      // Get all rows in the table
      const table = soplRow.closest('table');
      if (!table) return 0;
      
      const allRows = Array.from(table.querySelectorAll('tr'));
      const soplRowIndex = allRows.indexOf(soplRow);
      
      // Get 3 rows above SOPL
      let total = 0;
      for (let i = 1; i <= 3 && soplRowIndex - i >= 0; i++) {
        const rowAbove = allRows[soplRowIndex - i];
        const inputAbove = rowAbove.querySelector(
          `input[name^="Attributable-"][data-year-end="${standardContext}"]`
        ) as HTMLInputElement;
        
        if (inputAbove && inputAbove.value && inputAbove.value.trim() !== '') {
          const numValue = parseFloat(inputAbove.value.replace(/[^\d.-]/g, '')) || 0;
          total += numValue;
        }
      }
      
      return total;
    }
    
    if (fieldName.startsWith('SOCI-')) {
      // SOCI: Sum 2 rows above (2 Attributable- fields)
      // Find the row position of SOCI field, then get 2 inputs above it
      const sociRow = targetInput.closest('tr');
      if (!sociRow) return 0;
      
      // Get all rows in the table
      const table = sociRow.closest('table');
      if (!table) return 0;
      
      const allRows = Array.from(table.querySelectorAll('tr'));
      const sociRowIndex = allRows.indexOf(sociRow);
      
      // Get 2 rows above SOCI
      let total = 0;
      for (let i = 1; i <= 2 && sociRowIndex - i >= 0; i++) {
        const rowAbove = allRows[sociRowIndex - i];
        const inputAbove = rowAbove.querySelector(
          `input[name^="Attributable-"][data-year-end="${standardContext}"]`
        ) as HTMLInputElement;
        
        if (inputAbove && inputAbove.value && inputAbove.value.trim() !== '') {
          const numValue = parseFloat(inputAbove.value.replace(/[^\d.-]/g, '')) || 0;
          total += numValue;
        }
      }
      
      return total;
    }

    // SORE: RetainedEarningsRestated = sum of 2 fields above
    if (fieldName === 'RetainedEarningsRestated') {
      const restatedRow = targetInput.closest('tr');
      if (!restatedRow) return 0;
      
      // Get all rows in the table
      const table = restatedRow.closest('table');
      if (!table) return 0;
      
      const allRows = Array.from(table.querySelectorAll('tr'));
      const restatedRowIndex = allRows.indexOf(restatedRow);
      
      // Sum 2 rows above
      let total = 0;
      for (let i = 1; i <= 2 && restatedRowIndex - i >= 0; i++) {
        const rowAbove = allRows[restatedRowIndex - i];
        const inputAbove = rowAbove.querySelector(
          `input[data-year-end="${standardContext}"]`
        ) as HTMLInputElement;
        
        if (inputAbove && inputAbove.value && inputAbove.value.trim() !== '') {
          const numValue = parseFloat(inputAbove.value.replace(/[^\d.-]/g, '')) || 0;
          total += numValue;
        }
      }
      
      return total;
    }
    
    // Check if this is a ClosingCash field
    // Cash closing calculation: OpeningCash + IncreaseDecreaseInCashAndCashEquivalents
    // All three fields must be in the same column (same data-year-end / standard context)
    if (fieldName.startsWith('ClosingCash-')) {
      // Find the closing cash input to get its standard context (data-year-end)
      const closingInput = document.querySelector(
        `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
      ) as HTMLInputElement;
      
      if (!closingInput) {
        return 0;
      }
      
      const standardContext = closingInput.getAttribute('data-year-end');
      if (!standardContext) {
        return 0;
      }
      
      // Find OpeningCash in the same column (same data-year-end)
      const openingInput = document.querySelector(
        `input[name="OpeningCash-CashAndCashEquivalents"][data-year-end="${standardContext}"]`
      ) as HTMLInputElement;
      
      // Find IncreaseDecreaseInCashAndCashEquivalents in the same column (same data-year-end)
      const increaseDecreaseInput = document.querySelector(
        `input[name="IncreaseDecreaseInCashAndCashEquivalents"][data-year-end="${standardContext}"]`
      ) as HTMLInputElement;
      
      let openingCash = 0;
      let increaseDecrease = 0;
      
      // Get OpeningCash value from DOM (it's an editable field)
      if (openingInput && openingInput.value && openingInput.value.trim() !== '') {
        openingCash = parseFloat(openingInput.value.replace(/[^\d.-]/g, '')) || 0;
      }
      
      // Get IncreaseDecreaseInCashAndCashEquivalents value
      // This is a readonly field calculated by HyperFormula
      if (increaseDecreaseInput && increaseDecreaseInput.value && increaseDecreaseInput.value.trim() !== '') {
        increaseDecrease = parseFloat(increaseDecreaseInput.value.replace(/[^\d.-]/g, '')) || 0;
      } else {
        // Fallback: try to get from HyperFormula using standard context
        const hyperFormulaValue = hyperFormula.getCalculatedValue('IncreaseDecreaseInCashAndCashEquivalents', standardContext);
        increaseDecrease = hyperFormulaValue || 0;
      }
      
      const closingCash = openingCash + increaseDecrease;
      
      return closingCash;
    }

    // SORE: Closing RetainedEarnings = RetainedEarningsRestated + IncreaseDecreaseInRetainedEarnings
    if (fieldName.startsWith('SORE-Closing-')) {
      // Read RetainedEarningsRestated from ref (our custom calculation, already in ref)
      const restatedRefKey = `RetainedEarningsRestated_${standardContext}`;
      let restatedValue = 0;
      if (parentFormData && parentFormData[restatedRefKey]) {
        restatedValue = parseFloat(parentFormData[restatedRefKey].replace(/[^\d.-]/g, '')) || 0;
      }
      
      // Read IncreaseDecreaseInRetainedEarnings from ref (HyperFormula calculation, already in ref)
      const increaseDecreaseRefKey = `IncreaseDecreaseInRetainedEarnings_${standardContext}`;
      let increaseDecreaseValue = 0;
      if (parentFormData && parentFormData[increaseDecreaseRefKey]) {
        increaseDecreaseValue = parseFloat(parentFormData[increaseDecreaseRefKey].replace(/[^\d.-]/g, '')) || 0;
      }
      
      const closingRetained = restatedValue + increaseDecreaseValue;
      
      return closingRetained;
    }
    
    // For normal HyperFormula fields, convert real context to standard context
    // Find the input to get its data-year-end (standard context)
    const input = document.querySelector(
      `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
    ) as HTMLInputElement;
    
    if (input) {
      const standardContext = input.getAttribute('data-year-end');
      if (standardContext) {
        // Call HyperFormula with STANDARD context (for performance)
        return hyperFormula.getCalculatedValue(fieldName, standardContext);
      }
    }
    
    // Fallback: try with real context (shouldn't happen if inputs are properly set up)
    return hyperFormula.getCalculatedValue(fieldName, contextRef);
  }, [hyperFormula.isReady, hyperFormula.getCalculatedValue]);

  // Helper function to update ClosingCash when OpeningCash or IncreaseDecreaseInCashAndCashEquivalents changes
  const updateClosingCash = useCallback((standardContext: string) => {
    const closingCashInputs = document.querySelectorAll(
      `input[name^="ClosingCash-"][data-year-end="${standardContext}"]`
    ) as NodeListOf<HTMLInputElement>;
    
    closingCashInputs.forEach(closingInput => {
      const closingFieldName = closingInput.getAttribute('name');
      const closingContextRef = closingInput.getAttribute('data-context-ref');
      if (closingFieldName && closingContextRef) {
        const calculatedValue = getCalculatedValue(closingFieldName, closingContextRef);
        if (calculatedValue !== null) {
          const formattedValue = numeral(calculatedValue).format('0,0.00');
          closingInput.value = formattedValue;
          // CRITICAL: Store in ref via globalFieldChange so validation can read it
          if (globalFieldChange) {
            globalFieldChange(closingFieldName, closingContextRef, formattedValue);
          }
          closingInput.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
            detail: { fieldName: closingFieldName, contextRef: closingContextRef, value: formattedValue }
          }));
        }
      }
    });
  }, [getCalculatedValue, globalFieldChange]);

  const handleFieldChange = useCallback((name: string, contextRef: string, value: string) => {
    // contextRef here is the REAL XBRL context (asof_20250630_SeparateMember, etc.)
    // UNCONTROLLED: DOM is already updated, just sync with HyperFormula
    if (globalFieldChange) {
      globalFieldChange(name, contextRef, value);
    }
    
    // For HyperFormula, convert real context to standard context
    // Find the input element to get its data-year-end (standard context)
    if (hyperFormula.isReady && hyperFormula.handleFieldChange) {
      // Find the input to get its standard context
      const input = document.querySelector(
        `input[name="${name}"][data-context-ref="${contextRef}"]`
      ) as HTMLInputElement;
      
      if (input) {
        const standardContext = input.getAttribute('data-year-end');
        if (standardContext) {
          // Call HyperFormula with STANDARD context (for performance)
          hyperFormula.handleFieldChange(name, standardContext, value);
          
          // Pure DOM updates for custom formulas (SOPL/SOCI/ClosingCash) - like useHyperFormula.ts
          // When Attributable- fields change, update SOPL/SOCI in same column
          if (name.startsWith('Attributable-')) {
            const soplInputs = document.querySelectorAll(
              `input[name^="SOPL-"][data-year-end="${standardContext}"]`
            ) as NodeListOf<HTMLInputElement>;
            
            const sociInputs = document.querySelectorAll(
              `input[name^="SOCI-"][data-year-end="${standardContext}"]`
            ) as NodeListOf<HTMLInputElement>;
            
            // Update SOPL fields - update DOM AND ref
            soplInputs.forEach(soplInput => {
              const soplFieldName = soplInput.getAttribute('name');
              const soplContextRef = soplInput.getAttribute('data-context-ref');
              if (soplFieldName && soplContextRef) {
                const calculatedValue = getCalculatedValue(soplFieldName, soplContextRef);
                if (calculatedValue !== null) {
                  const formattedValue = numeral(calculatedValue).format('0,0.00');
                  soplInput.value = formattedValue;
                  // CRITICAL: Store in ref via globalFieldChange so validation can read it
                  if (globalFieldChange) {
                    globalFieldChange(soplFieldName, soplContextRef, formattedValue);
                  }
                  soplInput.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
                    detail: { fieldName: soplFieldName, contextRef: soplContextRef, value: formattedValue }
                  }));
                }
              }
            });
            
            // Update SOCI fields - update DOM AND ref
            sociInputs.forEach(sociInput => {
              const sociFieldName = sociInput.getAttribute('name');
              const sociContextRef = sociInput.getAttribute('data-context-ref');
              if (sociFieldName && sociContextRef) {
                const calculatedValue = getCalculatedValue(sociFieldName, sociContextRef);
                if (calculatedValue !== null) {
                  const formattedValue = numeral(calculatedValue).format('0,0.00');
                  sociInput.value = formattedValue;
                  // CRITICAL: Store in ref via globalFieldChange so validation can read it
                  if (globalFieldChange) {
                    globalFieldChange(sociFieldName, sociContextRef, formattedValue);
                  }
                  sociInput.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
                    detail: { fieldName: sociFieldName, contextRef: sociContextRef, value: formattedValue }
                  }));
                }
              }
            });
          }
          
          // When OpeningCash changes, update ClosingCash
          if (name.startsWith('OpeningCash-')) {
            updateClosingCash(standardContext);
          }
        }
      }
    }
  }, [globalFieldChange, hyperFormula.isReady, hyperFormula.handleFieldChange, getCalculatedValue, updateClosingCash]);

  // Listen for ref updates to IncreaseDecreaseInCashAndCashEquivalents
  // When this field's ref is updated (by HyperFormula or user input), recalculate ClosingCash
  useEffect(() => {
    const handleIncreaseDecreaseCashUpdated = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { standardContext } = customEvent.detail || {};
      
      // When IncreaseDecreaseInCashAndCashEquivalents ref is updated, update ClosingCash
      if (standardContext) {
        updateClosingCash(standardContext);
      }
    };
    
    // Listen for the custom event dispatched when ref is updated
    document.addEventListener('increaseDecreaseCashUpdated', handleIncreaseDecreaseCashUpdated);
    
    return () => {
      document.removeEventListener('increaseDecreaseCashUpdated', handleIncreaseDecreaseCashUpdated);
    };
  }, [updateClosingCash]);

  // Helper to update RetainedEarningsRestated in a column
  const updateRetainedEarningsRestated = useCallback((standardContext: string) => {
    const restatedInputs = document.querySelectorAll(
      `input[name="RetainedEarningsRestated"][data-year-end="${standardContext}"]`
    ) as NodeListOf<HTMLInputElement>;

    restatedInputs.forEach(restatedInput => {
      const restatedContextRef = restatedInput.getAttribute('data-context-ref');
      if (restatedContextRef) {
        const calculatedValue = getCalculatedValue('RetainedEarningsRestated', restatedContextRef);
        if (calculatedValue !== null) {
          const formattedValue = numeral(calculatedValue).format('0,0.00');
          restatedInput.value = formattedValue;
          if (globalFieldChange) {
            globalFieldChange('RetainedEarningsRestated', restatedContextRef, formattedValue);
          }
          restatedInput.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
            detail: { fieldName: 'RetainedEarningsRestated', contextRef: restatedContextRef, value: formattedValue }
          }));
        }
      }
    });
  }, [getCalculatedValue, globalFieldChange]);

  // Helper to update SORE-Closing fields
  const updateSoreClosing = useCallback((standardContext: string) => {
    const closingInputs = document.querySelectorAll(
      `input[data-sore-source="closing"][data-year-end="${standardContext}"]`
    ) as NodeListOf<HTMLInputElement>;

    closingInputs.forEach(closingInput => {
      const closingFieldName = closingInput.getAttribute('name');
      const closingContextRef = closingInput.getAttribute('data-context-ref');
      if (closingFieldName && closingContextRef) {
        const calculatedValue = getCalculatedValue(closingFieldName, closingContextRef);
        if (calculatedValue !== null) {
          const formattedValue = numeral(calculatedValue).format('0,0.00');
          closingInput.value = formattedValue;
          if (globalFieldChange) {
            globalFieldChange(closingFieldName, closingContextRef, formattedValue);
          }
        }
      }
    });
  }, [getCalculatedValue, globalFieldChange]);

  // Listen for ANY input change in the table to update RetainedEarningsRestated
  useEffect(() => {
    const handleTableInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (!target || !target.classList.contains('financial-input')) return;
      
      const standardContext = target.getAttribute('data-year-end');
      if (standardContext) {
        // Recalculate RetainedEarningsRestated in this column
        updateRetainedEarningsRestated(standardContext);
      }
    };

    // Listen on table for any input change (debounced by FieldInput component)
    document.addEventListener('input', handleTableInput, true);

    return () => {
      document.removeEventListener('input', handleTableInput, true);
    };
  }, [updateRetainedEarningsRestated]);

  // Listen for hyperformulaUpdate to catch:
  // 1. When RetainedEarningsRestated is updated → update SORE-Closing
  // 2. When IncreaseDecreaseInRetainedEarnings is updated by HyperFormula → update SORE-Closing
  useEffect(() => {
    const handleHyperFormulaUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { fieldName } = customEvent.detail || {};
      const target = e.target as HTMLInputElement;
      const standardContext = target?.getAttribute('data-year-end');
      
      if (standardContext && (fieldName === 'RetainedEarningsRestated' || fieldName === 'IncreaseDecreaseInRetainedEarnings')) {
        updateSoreClosing(standardContext);
      }
    };

    document.addEventListener('hyperformulaUpdate', handleHyperFormulaUpdate, true);

    return () => {
      document.removeEventListener('hyperformulaUpdate', handleHyperFormulaUpdate, true);
    };
  }, [updateSoreClosing]);

  // Calculate and store SOPL/SOCI/ClosingCash fields in ref after initial data load
  // This ensures validation can read these calculated fields on initial load
  useEffect(() => {
    if (!hyperFormula.isReady || !globalFieldChange) {
      return;
    }

    const calculateAndStoreSpecialFields = () => {
      // Get all standard contexts from inputs in this table
      const standardContexts = new Set<string>();
      document.querySelectorAll('.table-element input[data-year-end]').forEach(el => {
        const standardContext = el.getAttribute('data-year-end');
        if (standardContext) {
          standardContexts.add(standardContext);
        }
      });

      // For each standard context, calculate and store special fields
      standardContexts.forEach(standardContext => {
        // Get all inputs with this standard context to find their contextRefs
        const inputs = document.querySelectorAll(
          `.table-element input[data-year-end="${standardContext}"]`
        ) as NodeListOf<HTMLInputElement>;

        const contextRefs = new Set<string>();
        inputs.forEach(input => {
          const contextRef = input.getAttribute('data-context-ref');
          if (contextRef) {
            contextRefs.add(contextRef);
          }
        });

        // Calculate and store SOPL fields
        contextRefs.forEach(contextRef => {
          const soplInputs = document.querySelectorAll(
            `.table-element input[name^="SOPL-"][data-context-ref="${contextRef}"]`
          ) as NodeListOf<HTMLInputElement>;

          soplInputs.forEach(soplInput => {
            const soplFieldName = soplInput.getAttribute('name');
            if (soplFieldName) {
              const calculatedValue = getCalculatedValue(soplFieldName, contextRef);
              if (calculatedValue !== null) {
                const formattedValue = numeral(calculatedValue).format('0,0.00');
                // Store in ref via globalFieldChange
                globalFieldChange(soplFieldName, contextRef, formattedValue);
              }
            }
          });
        });

        // Calculate and store SOCI fields
        contextRefs.forEach(contextRef => {
          const sociInputs = document.querySelectorAll(
            `.table-element input[name^="SOCI-"][data-context-ref="${contextRef}"]`
          ) as NodeListOf<HTMLInputElement>;

          sociInputs.forEach(sociInput => {
            const sociFieldName = sociInput.getAttribute('name');
            if (sociFieldName) {
              const calculatedValue = getCalculatedValue(sociFieldName, contextRef);
              if (calculatedValue !== null) {
                const formattedValue = numeral(calculatedValue).format('0,0.00');
                // Store in ref via globalFieldChange
                globalFieldChange(sociFieldName, contextRef, formattedValue);
              }
            }
          });
        });

        // Calculate and store ClosingCash fields
        updateClosingCash(standardContext);

        // Calculate and store RetainedEarningsRestated fields
        contextRefs.forEach(contextRef => {
          const restatedInputs = document.querySelectorAll(
            `.table-element input[name="RetainedEarningsRestated"][data-context-ref="${contextRef}"]`
          ) as NodeListOf<HTMLInputElement>;

          restatedInputs.forEach(restatedInput => {
            const calculatedValue = getCalculatedValue('RetainedEarningsRestated', contextRef);
            if (calculatedValue !== null) {
              const formattedValue = numeral(calculatedValue).format('0,0.00');
              globalFieldChange('RetainedEarningsRestated', contextRef, formattedValue);
            }
          });
        });

        // Calculate and store SORE-Closing fields (after Restated is calculated)
        contextRefs.forEach(contextRef => {
          const closingInputs = document.querySelectorAll(
            `.table-element input[data-sore-source="closing"][data-context-ref="${contextRef}"]`
          ) as NodeListOf<HTMLInputElement>;

          closingInputs.forEach(closingInput => {
            const closingFieldName = closingInput.getAttribute('name');
            if (closingFieldName) {
              const calculatedValue = getCalculatedValue(closingFieldName, contextRef);
              if (calculatedValue !== null) {
                const formattedValue = numeral(calculatedValue).format('0,0.00');
                globalFieldChange(closingFieldName, contextRef, formattedValue);
              }
            }
          });
        });
      });
    };

    // Listen for formDataLoaded event - calculate after data is loaded
    const handleFormDataLoaded = () => {
      // Wait for HyperFormula to finish calculating all values
      setTimeout(() => {
        calculateAndStoreSpecialFields();
      }, 1500);
    };

    document.addEventListener('formDataLoaded', handleFormDataLoaded);

    // Also calculate after a delay as fallback
    const timeoutId = setTimeout(() => {
      calculateAndStoreSpecialFields();
    }, 2000);

    return () => {
      document.removeEventListener('formDataLoaded', handleFormDataLoaded);
      clearTimeout(timeoutId);
    };
  }, [hyperFormula.isReady, globalFieldChange, getCalculatedValue, updateClosingCash]);

  // Define columns based on company info (exactly like original macro)
  const columns: Column[] = [];
  
  if (companyInfo.consolidated === 'Y') {
    columns.push({
      label: 'Current Year Consolidated',
      member: '_ConsolidatedMember',
      period: 'current'
    });
    
    if (companyInfo.first_year === 'N') {
      columns.push({
        label: 'Previous Year Consolidated',
        member: '_ConsolidatedMember',
        period: 'comparative'
      });
    }
    
    columns.push({
      label: 'Current Year Separate',
      member: '_SeparateMember',
      period: 'current'
    });
    
    if (companyInfo.first_year === 'N') {
      columns.push({
        label: 'Previous Year Separate',
        member: '_SeparateMember',
        period: 'comparative'
      });
    }
  } else {
    columns.push({
      label: 'Current Year',
      member: '_SeparateMember',
      period: 'current'
    });
    
    if (companyInfo.first_year === 'N') {
      columns.push({
        label: 'Previous Year',
        member: '_SeparateMember',
        period: 'comparative'
      });
    }
  }

  // Find the actual line items in the section structure
  let lineItems: any[] = [];
  
  if (section.structure?.children) {
    // Find line items container (matches original macro logic)
    const lineItemsContainer = section.structure.children.find((child: any) => 
      child.name.includes('LineItems') || child.name.includes('Abstract')
    );
    
    if (lineItemsContainer && lineItemsContainer.structure?.children) {
      lineItems = lineItemsContainer.structure.children;
    } else {
      lineItems = section.structure.children;
    }
  }

  return (
    <div className="card">

      <div className="card-table">
        <table className="table-element" data-datatable-table="true">
          <thead>
            <tr>
              <th>
                <span className="sort">
                  <span className="sort-label"></span>
                </span>
              </th>
              {columns.map((column, index) => (
                <th key={index}>
                  <span className="sort">
                    <span className="sort-label">{column.label}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
             {lineItems.map((item: any, index: number) => (
               <LineItem
                 key={`${item.name}_${item.qname}_${index}`}
                 item={item}
                 columns={columns}
                 level={0}
                 negativeTargets={negativeTargets}
                 readOnlyFields={hyperFormula.readOnlyFields}
                 companyInfo={companyInfo}
                 formData={parentFormData || {}}
                 onFieldChange={handleFieldChange}
                 onGetCalculatedValue={getCalculatedValue}
                 getTestOccurrence={getTestOccurrence}
                 itemIndex={index}
                 onFootnoteClick={onFootnoteClick}
               />
             ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});